import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/useAuth'
import { useTheme } from '../../contexts/ThemeContext'
import { useMetasContext } from '../../contexts/MetasContext'
import { useGastosContext } from '../../contexts/GastosContext'
import { useProfileContext } from '../../contexts/ProfileContext'
import ThemeSelector from '../ui/ThemeSelector'
import { IcBell, IcTarget, IcSparkle, IcHeart, IcArrowRight } from '../ui/Icons'

export default function Perfil() {
  const navigate = useNavigate()
  const { session, signOut } = useAuth()
  const { theme } = useTheme()
  const { metas } = useMetasContext()
  const { gastos, totales } = useGastosContext()
  const { profile, updateProfile, deleteAccount } = useProfileContext()
  const [confirmando, setConfirmando] = useState(false)
  const [miembro, setMiembro] = useState('')

  // Edit profile state
  const [editando, setEditando] = useState(false)
  const [editNombre, setEditNombre] = useState('')
  const [editGenero, setEditGenero] = useState('')
  const [editFechaNac, setEditFechaNac] = useState('')
  const [guardando, setGuardando] = useState(false)

  // Delete account state
  const [confirmandoEliminar, setConfirmandoEliminar] = useState(false)
  const [deleteError, setDeleteError] = useState('')
  const [eliminando, setEliminando] = useState(false)

  useEffect(() => {
    if (session?.user?.created_at) {
      const fecha = new Date(session.user.created_at)
      setMiembro(fecha.toLocaleDateString('es', { month: 'long', year: 'numeric' }))
    }
  }, [session])

  const email = session?.user?.email ?? ''
  const inicial = (profile?.nombre || email).charAt(0).toUpperCase()
  const s = makeStyles(theme)

  const pronombre = profile?.genero === 'femenino' ? 'a' : profile?.genero === 'masculino' ? 'o' : ''

  function startEdit() {
    setEditNombre(profile?.nombre || '')
    setEditGenero(profile?.genero || '')
    setEditFechaNac(profile?.fecha_nacimiento || '')
    setEditando(true)
  }

  async function handleSaveProfile() {
    setGuardando(true)
    await updateProfile({
      nombre: editNombre || null,
      genero: editGenero || 'otro',
      fecha_nacimiento: editFechaNac || null,
    })
    setGuardando(false)
    setEditando(false)
  }

  async function handleSignOut() {
    if (!confirmando) { setConfirmando(true); return }
    await signOut()
  }

  async function handleDeleteAccount() {
    if (!confirmandoEliminar) { setConfirmandoEliminar(true); return }
    setEliminando(true)
    const { error } = await deleteAccount()
    if (error) {
      setDeleteError('no se pudo eliminar la cuenta. asegúrate de haber ejecutado el SQL delete_user() en Supabase.')
      setConfirmandoEliminar(false)
      setEliminando(false)
    } else {
      await signOut()
    }
  }

  const menu = [
    { Icon: IcBell,    title: 'Recordatorios suaves', sub: 'una vez al día, nunca más',   pronto: true },
    { Icon: IcTarget,  title: 'Mis categorías',       sub: 'personaliza tu lenguaje',      pronto: true },
    { Icon: IcSparkle, title: 'Florece Premium',      sub: 'metas ilimitadas, insights',   onClick: () => navigate('/premium') },
    { Icon: IcHeart,   title: 'Cuéntanos cómo vas',   sub: 'tu voz da forma al jardín',    onClick: () => window.open('mailto:hola@florece.app', '_blank') },
  ]

  return (
    <div style={s.container}>
      <p style={s.eyebrow}>este es tu rincón</p>
      <h1 style={s.titulo}>yo</h1>

      {/* Hero card */}
      <div style={s.profHero}>
        <div style={s.profAv}>{inicial}</div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={s.profNm}>{profile?.nombre || email.split('@')[0]}</div>
          {profile?.genero && profile.genero !== 'otro' && (
            <div style={s.profGenero}>{profile.genero === 'femenino' ? 'ella · ' : 'él · '}{email}</div>
          )}
          {(!profile?.genero || profile.genero === 'otro') && (
            <div style={s.profEm}>{email}</div>
          )}
          {miembro && <div style={s.profDt}>floreciendo desde {miembro}</div>}
        </div>
      </div>

      {/* Edit profile */}
      {editando ? (
        <div style={s.editCard}>
          <div style={s.editField}>
            <label style={s.editLabel}>nombre</label>
            <input value={editNombre} onChange={e => setEditNombre(e.target.value)}
              placeholder="tu nombre" style={s.editInput} />
          </div>
          <div style={s.editField}>
            <label style={s.editLabel}>¿cómo te identifies?</label>
            <div style={s.generoRow}>
              {[{val:'femenino',label:'ella'},{val:'masculino',label:'él'},{val:'otro',label:'libre'}].map(({val,label}) => (
                <button key={val} type="button" onClick={() => setEditGenero(val)}
                  style={{...s.generoPill, ...(editGenero === val ? s.generoPillActive : {})}}>
                  {label}
                </button>
              ))}
            </div>
          </div>
          <div style={s.editField}>
            <label style={s.editLabel}>fecha de nacimiento</label>
            <input type="date" value={editFechaNac} onChange={e => setEditFechaNac(e.target.value)}
              style={s.editInput} />
          </div>
          <div style={s.editActions}>
            <button style={s.editCancel} onClick={() => setEditando(false)}>cancelar</button>
            <button style={s.editSave} onClick={handleSaveProfile} disabled={guardando}>
              {guardando ? 'guardando...' : 'guardar ✦'}
            </button>
          </div>
        </div>
      ) : (
        <button style={s.editBtn} onClick={startEdit}>editar perfil</button>
      )}

      {/* Stats */}
      <div style={s.statRow}>
        <div style={s.statCard}>
          <div style={s.statN}>{metas.length}</div>
          <div style={s.statL}>metas</div>
        </div>
        <div style={s.statCard}>
          <div style={s.statN}>
            {totales.ahorros >= 1000 ? `${Math.round(totales.ahorros / 1000)}k` : `$${totales.ahorros}`}
          </div>
          <div style={s.statL}>ahorrado</div>
        </div>
        <div style={s.statCard}>
          <div style={s.statN}>{gastos.length}</div>
          <div style={s.statL}>registros</div>
        </div>
      </div>

      {/* Selector de tema */}
      <div style={s.themeRow}>
        <p style={s.themeRowLbl}>tema</p>
        <ThemeSelector />
      </div>

      {/* Menú */}
      <div style={s.menuList}>
        {menu.map(({ Icon, title, sub, pronto, onClick }) => (
          <div key={title}
            style={{ ...s.menuRow, ...(pronto ? { opacity: 0.55, cursor: 'default' } : {}) }}
            onClick={!pronto ? onClick : undefined}>
            <div style={s.menuIc}><Icon size={18} /></div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={s.menuTitle}>{title}</div>
              <div style={s.menuSub}>{sub}</div>
            </div>
            {pronto
              ? <span style={s.prontoBadge}>pronto</span>
              : <IcArrowRight size={16} color={theme.inkLight} style={{ flexShrink: 0 }} />
            }
          </div>
        ))}
      </div>

      {/* Cerrar sesión */}
      <button
        style={{ ...s.logoutBtn, ...(confirmando ? s.logoutConfirm : {}) }}
        onClick={handleSignOut}
      >
        {confirmando ? `¿segur${pronombre}? toca de nuevo para salir` : pronombre ? `cerrar sesión, tranquil${pronombre}` : 'cerrar sesión con calma'}
      </button>
      {confirmando && (
        <button style={s.cancelBtn} onClick={() => setConfirmando(false)}>cancelar</button>
      )}

      {/* Eliminar cuenta — botón feo adrede */}
      <div style={s.deleteArea}>
        <p style={s.deleteLabel}>zona de peligro</p>
        <button
          style={{ ...s.deleteBtn, ...(confirmandoEliminar ? s.deleteBtnConfirm : {}) }}
          onClick={handleDeleteAccount}
          disabled={eliminando}
        >
          {eliminando ? 'eliminando...' : confirmandoEliminar ? '⚠ SÍ, ELIMINAR MI CUENTA PARA SIEMPRE' : 'ELIMINAR MI CUENTA'}
        </button>
        {confirmandoEliminar && (
          <button style={s.deleteCancelBtn} onClick={() => { setConfirmandoEliminar(false); setDeleteError('') }}>
            no, quedarme
          </button>
        )}
        {deleteError && <p style={s.deleteErrorMsg}>{deleteError}</p>}
      </div>

    </div>
  )
}

const makeStyles = (t) => ({
  container:       { maxWidth: 560, margin: '0 auto', padding: '48px 24px 110px', fontFamily: "'DM Sans', sans-serif", background: t.cream, minHeight: '100vh' },
  eyebrow:         { fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 15, color: t.inkLight, margin: '0 0 4px' },
  titulo:          { fontFamily: "'Cormorant Garamond', serif", fontSize: 38, fontWeight: 300, color: t.ink, margin: '0 0 20px' },
  profHero:        { margin: '0 0 10px', padding: 20, borderRadius: 22, background: t.cardGradient, color: 'white', boxShadow: `0 16px 40px ${t.accentDeep}44`, display: 'flex', alignItems: 'center', gap: 14, overflow: 'hidden' },
  profAv:          { width: 64, height: 64, borderRadius: '50%', background: 'rgba(255,255,255,0.22)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: "'Cormorant Garamond',serif", fontStyle: 'italic', fontSize: 28, color: 'white', flexShrink: 0 },
  profNm:          { fontFamily: "'Cormorant Garamond',serif", fontSize: 22, fontWeight: 400 },
  profGenero:      { fontFamily: "'DM Sans',sans-serif", fontSize: 13, opacity: 0.85, marginTop: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  profEm:          { fontFamily: "'DM Sans',sans-serif", fontSize: 14, opacity: 0.85, marginTop: 4, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  profDt:          { fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 13, opacity: 0.9, marginTop: 6 },
  editBtn:         { display: 'block', width: '100%', padding: 10, background: 'none', border: `1.5px solid ${t.cardBorder}`, borderRadius: 12, fontFamily: "'DM Sans', sans-serif", fontSize: 13, color: t.inkLight, cursor: 'pointer', marginBottom: 14, textAlign: 'center' },
  editCard:        { background: t.paper, border: `1.5px solid ${t.cardBorder}`, borderRadius: 18, padding: 18, marginBottom: 14 },
  editField:       { marginBottom: 14 },
  editLabel:       { display: 'block', fontFamily: "'DM Sans',sans-serif", fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', color: t.inkLight, fontWeight: 500, marginBottom: 8 },
  editInput:       { width: '100%', padding: '12px 14px', borderRadius: 12, border: `1.5px solid ${t.inputBg}`, background: t.inputBg, fontFamily: "'DM Sans',sans-serif", fontSize: 14, color: t.ink, outline: 'none', boxSizing: 'border-box' },
  generoRow:       { display: 'flex', gap: 8 },
  generoPill:      { flex: 1, padding: '9px 8px', borderRadius: 12, border: `1.5px solid ${t.cardBorder}`, background: t.cream, fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: t.inkMid, cursor: 'pointer' },
  generoPillActive:{ background: t.accent + '18', borderColor: t.terra, color: t.terraDeep, fontWeight: 600 },
  editActions:     { display: 'flex', gap: 8 },
  editCancel:      { flex: 1, padding: 12, borderRadius: 12, border: `1.5px solid ${t.cardBorder}`, background: 'transparent', fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: t.inkMid, cursor: 'pointer' },
  editSave:        { flex: 1, padding: 12, borderRadius: 12, border: 'none', background: t.cardGradient, fontFamily: "'Cormorant Garamond',serif", fontStyle: 'italic', fontSize: 16, color: 'white', cursor: 'pointer' },
  statRow:         { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8, marginBottom: 14 },
  statCard:        { padding: '14px 10px', background: t.paper, border: `1.5px solid ${t.cardBorder}`, borderRadius: 16, textAlign: 'center' },
  statN:           { fontFamily: "'Cormorant Garamond',serif", fontSize: 22, fontWeight: 600, color: t.ink },
  statL:           { fontFamily: "'DM Sans',sans-serif", fontSize: 11, letterSpacing: 1, textTransform: 'uppercase', color: t.inkLight, marginTop: 4 },
  themeRow:        { marginBottom: 14, padding: 14, background: t.paper, border: `1.5px solid ${t.cardBorder}`, borderRadius: 16 },
  themeRowLbl:     { fontFamily: "'DM Sans',sans-serif", fontSize: 11, letterSpacing: 1.5, textTransform: 'uppercase', color: t.inkLight, fontWeight: 500, margin: '0 0 10px' },
  menuList:        { marginBottom: 16 },
  menuRow:         { padding: 14, background: t.paper, border: `1.5px solid ${t.txBorder}`, borderRadius: 14, display: 'flex', alignItems: 'center', gap: 12, marginBottom: 8, cursor: 'pointer' },
  menuIc:          { width: 36, height: 36, borderRadius: 11, background: t.accent + '1E', display: 'flex', alignItems: 'center', justifyContent: 'center', color: t.terra, flexShrink: 0 },
  menuTitle:       { fontFamily: "'DM Sans',sans-serif", fontSize: 14, fontWeight: 500, color: t.ink, lineHeight: 1.1 },
  menuSub:         { fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 13, color: t.inkLight, marginTop: 2 },
  prontoBadge:     { fontFamily: "'DM Sans',sans-serif", fontSize: 12, color: t.inkLight, background: t.paper, border: `1px solid ${t.txBorder}`, borderRadius: 999, padding: '2px 8px', flexShrink: 0 },
  logoutBtn:       { width: '100%', padding: 14, borderRadius: 14, border: 'none', background: 'none', fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 16, color: t.mauve, cursor: 'pointer' },
  logoutConfirm:   { color: t.mauve, fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 16 },
  cancelBtn:       { width: '100%', marginTop: 10, padding: 12, borderRadius: 14, border: 'none', background: 'transparent', fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: 15, color: t.inkLight, cursor: 'pointer', textDecoration: 'underline' },
  // delete zone — styled deliberately ugly to discourage use
  deleteArea:      { marginTop: 36, paddingTop: 20, borderTop: '1px dashed #ccc' },
  deleteLabel:     { fontFamily: 'monospace', fontSize: 9, textTransform: 'uppercase', color: '#aaa', letterSpacing: 0, textAlign: 'center', marginBottom: 10 },
  deleteBtn:       { width: '100%', padding: '8px 10px', borderRadius: 4, border: '1.5px solid #bbb', background: '#ebebeb', fontFamily: 'monospace', fontSize: 10, color: '#888', cursor: 'pointer', textTransform: 'uppercase' },
  deleteBtnConfirm:{ background: '#fff0f0', borderColor: '#cc2222', color: '#cc2222' },
  deleteCancelBtn: { marginTop: 8, display: 'block', width: '100%', padding: 10, borderRadius: 14, border: 'none', background: t.cardGradient, fontFamily: "'Cormorant Garamond',serif", fontStyle: 'italic', fontSize: 16, color: 'white', cursor: 'pointer', boxShadow: `0 8px 20px ${t.accentDeep}33` },
  deleteErrorMsg:  { fontFamily: "'DM Sans',sans-serif", fontSize: 13, color: '#cc2222', textAlign: 'center', marginTop: 8, lineHeight: 1.4 },
})
